import React, { useCallback, useRef } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import { BodyText, Icon, theme, TitleText, usePressableState, useResponsive } from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useTelemetry } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

export const OrderStatusLoyaltyPointsBanner = () => {
  const ref = useRef(null);
  const { isInteracting } = usePressableState(ref);
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const router = useRouter();

  // ─── Content ───────────────────────────────────────────────────────────────

  const content = useContentfulContentTypeEntry<LoyaltyContainer>({
    contentType: 'loyalty',
  });

  const {
    orderStatusPointsBannerTitle: title = formatMessage(messages.title),
    orderStatusPointsBannerBody: body = formatMessage(messages.body),
  } = content.data?.fields ?? {};

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handlePress = useCallback(() => {
    track('order_status.sg-rewards-cta.press');

    router.dismissTo('/rewards');
  }, [track, router]);

  // ─── Styles ─────────────────────────────────────────────────────────────────

  const containerStyle = [styles.container, isInteracting && styles.containerInteracting];
  const { screenDimensions, currentBreakpoint } = useResponsive();
  const isSmallerXS = screenDimensions.width < 360;
  const shouldRenderIcon = !isSmallerXS && !currentBreakpoint.isSM;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={ref}
      style={containerStyle}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={formatMessage(messages.a11yLabel)}
    >
      <View style={styles.info}>
        <TitleText sizeMatch={['18']} style={styles.body}>
          {title}
        </TitleText>

        <BodyText sizeMatch={['12']} style={styles.description}>
          {body}
        </BodyText>
      </View>

      {shouldRenderIcon ? (
        <View style={styles.iconWrapper}>
          <Icon name="IconSgRewardSweetCornOutline" width={24} height={24} />

          <BodyText sizeMatch={['14']} style={styles.body}>
            <FormattedMessage {...messages.rewards} />
          </BodyText>
        </View>
      ) : null}
    </Pressable>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Points on the way!',
    description: 'Order Status | Loyalty Points Banner | Title',
  },
  body: {
    defaultMessage: 'Check your account in 24h.',
    description: 'Order Status | Loyalty Points Banner | Body',
  },
  rewards: {
    defaultMessage: 'Rewards',
    description: 'Order Status | Loyalty Points Banner | Rewards',
  },
  a11yLabel: {
    defaultMessage: 'Open loyalty home',
    description: 'Order Status | Loyalty Points Banner | Accessibility Label',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.FOREST,
    borderRadius: theme.radius.xlarge,
    padding: theme.spacing['6'],
  },
  containerInteracting: {
    backgroundColor: theme.colors.KALE,
  },
  info: {
    flex: 1,
    gap: theme.spacing['2'],
  },
  body: {
    color: theme.colors.WHITE,
  },
  description: {
    color: theme.colors.OPACITY.WHITE.DARKEST,
  },
  iconWrapper: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    gap: theme.spacing['2'],
    borderWidth: 1,
    borderColor: theme.colors.WHITE,
    borderRadius: theme.radius.xlarge,
    paddingHorizontal: theme.spacing['4'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyContainer = {
  orderStatusPointsBannerTitle?: string;
  orderStatusPointsBannerBody?: string;
};
